<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" persistent max-width="850" content-class="round-2xl")
  v-card
    v-card-title.px-4.py-3
      v-row(no-gutters align="center")
        v-col(cols="10" offset="1")
          .text-md.font-semibold.text-center {{ parentItem ? 'Edit' : 'Add' }} family
        v-col(cols="1" align="right")
          v-btn(icon @click="closeDialog" small color="primary")
            v-icon mdi-close
    v-divider
    div.pa-6
      template(v-if="isFetching")
        v-skeleton-loader.mb-4(type="text@3")
        v-skeleton-loader(type="text@3")
      template(v-else-if="isError")
        div.text-center
          v-icon.mb-2(size="30" color="danger500") mdi-alert-circle-outline
          .text-sm.danger500--text Opss.. Unable to get family relationship
          .text-sm.danger500--text Please try again later
      template(v-else)
        v-row.mb-4(v-for="(relationship, relationshipIdx) in relationships" :key="relationshipIdx" no-gutters)
          v-col.mr-4
            .text-sm.font-medium.neutral500--text.mb-2 Patient
            patient-dropdown(v-model="relationship.patient" :disabledItems="disabledItems" height="40")
          v-col
            .text-sm.font-medium.neutral500--text.mb-2 Who is this person to {{ patientName }}?
            div.d-flex.align-center
              v-autocomplete.text-sm.round-xl(v-model="relationship.relationship" :items="relationshipOptions" item-text="name" item-value="id" placeholder="Select relationship" outlined dense height="40" hide-details="auto")
                template(#append)
                  v-icon.icon-height(size="20") $yzi-chevron-down
              y-button.ml-2(v-if="relationshipIdx > 0" outlined color="neutral400" background-color="var(--v-neutral100)" prepend-icon="$yzi-trash-03" icon-color="danger500" icon-size="20" unset-min-width size="sm" button-class="px-2" @click="deleteRelationship(relationshipIdx)")
        y-button.mt-2(label="Add more" outlined color="neutral400" prepend-icon="$yzi-plus" icon-size="16" icon-color="neutral500" text-color="neutral700" size="xs" height="28" unset-min-width button-class="px-2" :disabled="!canAddMoreOrSave" @click="addMore")
    v-divider
    v-card-actions.py-3.px-4
      v-spacer
      y-button.mr-3(:label="$t('cancel')" text color="neutral700" size="sm" button-class="px-0" @click="closeDialog")
      y-button(label="Save" color="neutral700" size="sm" button-class="px-3" dark :loading="isLoading" @click="handleSave")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PatientDropdown from '@/components/clinic/relationship/PatientDropdown.vue'

export default {
  components: {
    PatientDropdown,
  },
  data() {
    return {
      isFetching: false,
      isError: false,
      isLoading: false,
      relationship: null,
      existingRelationships: [],
      searchPatient: null,
      relationshipOptions: null,
      relationships: [
        {
          patient: null,
          relationship: null
        }
      ],
      postRequestResults: {
        successCount: 0,
        failedCount: 0
      }
    }
  },
  props: {
    open: {
      type: Boolean,
    },
    patientId: {
      type: Number,
      required: true
    },
    patientName: {
      type: String,
      default: ''
    },
    parentItem: {
      type: Object,
      default: null
    }
  },
  async mounted () {
    try {
      this.isFetching = true
      const allFetching = [this.fetchRelationshipOptions(), this.fetchPatientRelationships()]
      await Promise.all([...allFetching])
      // Prefill data to edit
      if (this.parentItem) {
        this.relationships[0].patient = this.parentItem.related_person
        this.relationships[0].relationship = this.parentItem.relationship.id
      }
    } catch (error) {
      this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      this.isError = true
    } finally {
      this.isFetching = false
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.open;
      },
      set(newValue) {
        this.$emit("update:open", newValue);
      },
    },
    canAddMoreOrSave () {
      return this.relationships.every((data) => data.patient && data.relationship)
    },
    disabledItems () {
      // Existing relationships
      let existingRelationships = [...this.existingRelationships]
      if (this.parentItem) {
        existingRelationships = existingRelationships.filter((item) => item?.related_person !== this.parentItem.related_person.id)
      }
      return [
        {
          id: this.patientId,
          reason: 'Own name'
        },
        ...existingRelationships.map((item) => {
          return {
            id: item?.related_person,
            reason: item?.relationship.name
          }
        }),
        // In selection
        ...this.relationships.map((item) => {
          return {
            id: item?.patient?.id,
            reason: 'Selected'
          }
        })
      ]
    },
    payloads () {
      return this.relationships.filter(data => data.patient && data.relationship).map((data) => {
        return {
          primary_person: this.patientId,
          related_person: data.patient.id,
          relationship: data.relationship
        }
      })
    }
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    closeDialog () {
      this.$emit('closed')
      this.showDialog = false
    },
    fetchRelationshipOptions () {
      return this.$axios.get('/clinics/relationship_options')
        .then(({ data }) => this.relationshipOptions = data.results)
    },
    fetchPatientRelationships () {
      const params = {
        expand: 'relationship',
        patient: this.patientId
      }
      return this.$axios.get(`/clinics/${this.company.slug}/patient_relationships`, { params })
        .then(({ data }) => this.existingRelationships = data.results)
    },
    addMore () {
      this.relationships.push({
        patient: null,
        relationship: null
      })
    },
    deleteRelationship (idx) {
      this.relationships.splice(idx, 1)
    },
    async handleSave () {
      this.isLoading = true
      for (const payload of this.payloads) {
        try {
          let endpoint
          let resp
          let method
          if (this.parentItem) {
            endpoint = `/clinics/${this.company.slug}/patient_relationships/${this.parentItem.id}`
            resp = await this.$axios.patch(endpoint, payload)
            method = 'patch'
          } else {
            endpoint = `/clinics/${this.company.slug}/patient_relationships`
            resp = await this.$axios.post(endpoint, payload)
            method = 'post'
          }
          this.postRequestResults.successCount++
          this.updateActivityLog(endpoint, payload, resp, method, resp.data.id)
        } catch (error) {
          this.postRequestResults.failedCount++
        }
      }
      // For success
      if (this.postRequestResults.successCount > 0) {
        if (this.postRequestResults.failedCount > 0) {
          this.showMessage(['warning', 'Some family relationship cannot be saved'])
        } else {
          this.showMessage(['success', 'Family successfully added'])
        }
        this.closeDialog()
        this.$emit('saved')
        return
      }
      // For failed
      if (this.postRequestResults.failedCount > 0) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.closeDialog()
        this.$emit('saved')
      }
    },
    updateActivityLog (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'family_relationship',
        activity: `${this.$options.filters.capitalize(method)} family relationship (#${id})`,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
  },
};
</script>

<style scoped>
.icon-height {
  margin-top: 2px
}
</style>
